import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, User, MapPin, Baby, Sparkles, ShieldCheck, Camera, LogOut, Crown, Trash2, Plus, AlertTriangle, Utensils } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = [
  "Outdoor Play", "Arts & Crafts", "Sports", "Reading", "Music", "Science",
  "Nature Walks", "Board Games", "Swimming", "Cooking", "Dance", "Building/LEGO"
];

const ALLERGENS = [
  "Nuts", "Peanuts", "Tree Nuts", "Dairy", "Eggs", "Gluten", "Wheat", 
  "Soy", "Fish", "Shellfish", "Sesame", "Mustard"
];

const DIETARY_PREFERENCES = [
  "Halal", "Kosher", "Vegetarian", "Vegan", "Pescatarian", 
  "Gluten-Free", "Dairy-Free", "No Pork", "No Beef"
];

const Profile = () => {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Form state
  const [parentName, setParentName] = useState("");
  const [bio, setBio] = useState("");
  const [locationCity, setLocationCity] = useState("");
  const [interests, setInterests] = useState([]);
  const [allergens, setAllergens] = useState([]);
  const [dietaryPreferences, setDietaryPreferences] = useState([]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/profile`);
        setProfile(response.data);
        setParentName(response.data.parent_name || "");
        setBio(response.data.bio || "");
        setLocationCity(response.data.location_city || "");
        setInterests(response.data.interests || []);
        setAllergens(response.data.allergens || []);
        setDietaryPreferences(response.data.dietary_preferences || []);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const toggleInterest = (interest) => {
    if (interests.includes(interest)) {
      setInterests(interests.filter(i => i !== interest));
    } else if (interests.length < 6) {
      setInterests([...interests, interest]);
    }
  };

  const toggleAllergen = (allergen) => {
    if (allergens.includes(allergen)) {
      setAllergens(allergens.filter(a => a !== allergen));
    } else {
      setAllergens([...allergens, allergen]);
    }
  };

  const toggleDietaryPreference = (pref) => {
    if (dietaryPreferences.includes(pref)) {
      setDietaryPreferences(dietaryPreferences.filter(p => p !== pref));
    } else {
      setDietaryPreferences([...dietaryPreferences, pref]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, {
        parent_name: parentName,
        bio,
        location_city: locationCity,
        interests,
        allergens,
        dietary_preferences: dietaryPreferences
      });
      toast.success("Profile updated!");
      setEditMode(false);
      
      // Refresh profile
      const response = await axios.get(`${API}/profile`);
      setProfile(response.data);
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyPhoto = async () => {
    try {
      await axios.post(`${API}/profile/verify-photo`);
      toast.success("Photo verification submitted!");
      
      // Refresh
      const response = await axios.get(`${API}/profile`);
      setProfile(response.data);
    } catch (error) {
      toast.error("Verification failed");
    }
  };

  const handleRemoveChild = async (childId) => {
    try {
      await axios.delete(`${API}/profile/children/${childId}`);
      toast.success("Child removed");
      
      // Refresh
      const response = await axios.get(`${API}/profile`);
      setProfile(response.data);
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F5] flex items-center justify-center pb-20">
        <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F5] pb-24" data-testid="profile-page">
      {/* Decorative blobs */}
      <div className="blob blob-purple w-64 h-64 -top-32 -right-32 fixed opacity-30" />
      <div className="blob blob-pink w-48 h-48 bottom-40 -left-24 fixed opacity-30" />

      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-100">
        <div className="max-w-lg mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-800">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Your Profile
            </h1>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editMode ? handleSave() : setEditMode(true)}
              disabled={saving}
              data-testid="edit-save-btn"
            >
              {editMode ? (saving ? "Saving..." : "Save") : "Edit"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-6 space-y-6">
        {/* Profile Header */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-sky-400 to-purple-400 flex items-center justify-center text-white text-3xl font-bold ring-4 ring-teal-100">
                {profile?.photo_url ? (
                  <img src={profile.photo_url} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  profile?.parent_name?.[0]
                )}
              </div>
              {profile?.is_photo_verified && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center border-2 border-white">
                  <ShieldCheck className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1">
              {editMode ? (
                <Input
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                  className="h-10 rounded-xl mb-2"
                  data-testid="parent-name-input"
                />
              ) : (
                <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {profile?.parent_name}
                </h2>
              )}
              <p className="text-slate-500 text-sm">{user?.email}</p>
              <div className="flex items-center gap-2 mt-1">
                {profile?.is_verified && (
                  <span className="text-xs bg-teal-100 text-teal-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </span>
                )}
                {user?.is_premium && (
                  <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Premium
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Photo verification */}
          {!profile?.is_photo_verified && (
            <button
              onClick={handleVerifyPhoto}
              className="mt-4 w-full p-3 bg-slate-50 rounded-xl flex items-center justify-center gap-2 text-slate-600 hover:bg-slate-100 transition-colors"
              data-testid="verify-photo-btn"
            >
              <Camera className="w-5 h-5" />
              <span className="text-sm font-medium">Verify your photo for trust badges</span>
            </button>
          )}
        </div>

        {/* Location */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-teal-500" />
            <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Location</h3>
          </div>
          {editMode ? (
            <Input
              value={locationCity}
              onChange={(e) => setLocationCity(e.target.value)}
              placeholder="City, State"
              className="h-12 rounded-xl"
              data-testid="location-input"
            />
          ) : (
            <p className="text-slate-600">{profile?.location_city || "Not set"}</p>
          )}
        </div>

        {/* Bio */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-pink-500" />
            <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>About Us</h3>
          </div>
          {editMode ? (
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell other families about yours..."
              className="rounded-xl min-h-[100px]"
              data-testid="bio-input"
            />
          ) : (
            <p className="text-slate-600">{profile?.bio || "No bio yet"}</p>
          )}
        </div>

        {/* Children */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Baby className="w-5 h-5 text-purple-500" />
              <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Kids</h3>
            </div>
          </div>
          {profile?.children?.length > 0 ? (
            <div className="space-y-3">
              {profile.children.map((child) => (
                <div key={child.child_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-800">{child.name}, {child.age} years old</p>
                    {child.interests?.length > 0 && (
                      <p className="text-sm text-slate-500">Loves: {child.interests.join(", ")}</p>
                    )}
                  </div>
                  {editMode && (
                    <button
                      onClick={() => handleRemoveChild(child.child_id)}
                      className="text-red-400 hover:text-red-500"
                      data-testid={`remove-child-${child.child_id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500">No children added yet</p>
          )}
        </div>

        {/* Interests */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Family Interests</h3>
          </div>
          {editMode ? (
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((interest) => (
                <button
                  key={interest}
                  onClick={() => toggleInterest(interest)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    interests.includes(interest)
                      ? 'bg-teal-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`interest-${interest}`}
                >
                  {interest}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {profile?.interests?.map((interest) => (
                <span key={interest} className="px-4 py-2 bg-teal-100 text-sky-700 rounded-full text-sm">
                  {interest}
                </span>
              ))}
              {(!profile?.interests || profile.interests.length === 0) && (
                <p className="text-slate-500">No interests set</p>
              )}
            </div>
          )}
        </div>

        {/* Allergens */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Allergens</h3>
          </div>
          <p className="text-sm text-amber-600 mb-3">Other families will see this when planning food activities.</p>
          {editMode ? (
            <div className="flex flex-wrap gap-2">
              {ALLERGENS.map((allergen) => (
                <button
                  key={allergen}
                  onClick={() => toggleAllergen(allergen)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    allergens.includes(allergen)
                      ? 'bg-amber-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-amber-100'
                  }`}
                  data-testid={`allergen-${allergen}`}
                >
                  {allergen}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {profile?.allergens?.map((allergen) => (
                <span key={allergen} className="px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm">
                  {allergen}
                </span>
              ))}
              {(!profile?.allergens || profile.allergens.length === 0) && (
                <p className="text-slate-500">No allergens specified</p>
              )}
            </div>
          )}
        </div>

        {/* Dietary Preferences */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Utensils className="w-5 h-5 text-green-500" />
            <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Dietary Preferences</h3>
          </div>
          {editMode ? (
            <div className="flex flex-wrap gap-2">
              {DIETARY_PREFERENCES.map((pref) => (
                <button
                  key={pref}
                  onClick={() => toggleDietaryPreference(pref)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    dietaryPreferences.includes(pref)
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-green-100'
                  }`}
                  data-testid={`dietary-${pref}`}
                >
                  {pref}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {profile?.dietary_preferences?.map((pref) => (
                <span key={pref} className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm">
                  {pref}
                </span>
              ))}
              {(!profile?.dietary_preferences || profile.dietary_preferences.length === 0) && (
                <p className="text-slate-500">No dietary preferences specified</p>
              )}
            </div>
          )}
        </div>

        {/* Premium CTA */}
        {!user?.is_premium && (
          <Link to="/subscription">
            <div className="premium-gradient rounded-3xl p-6 text-white shadow-lg" data-testid="premium-cta">
              <div className="flex items-center gap-4">
                <Crown className="w-10 h-10" />
                <div>
                  <h3 className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>Upgrade to Premium</h3>
                  <p className="opacity-90">Unlimited matches, see photos, plan playdates</p>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Logout */}
        <Button
          variant="outline"
          onClick={handleLogout}
          className="w-full rounded-xl py-6 text-red-500 border-red-200 hover:bg-red-50"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Log Out
        </Button>
      </main>

      <BottomNav />
    </div>
  );
};

export default Profile;